import type { FormEvent, MouseEvent, ReactNode } from 'react';
import { useCallback } from 'react';
import ModalFooter from '../ModalFooter';
import ModalHeader from '../ModalHeader';
import { ModalBody, Wrapper } from './styled';
import { Column, BlurFilter, LoadingWarren } from '@simplywallst/ui-core';

export interface Props {
  id: string;
  title: ReactNode;
  asForm: boolean;
  isNaked: boolean;
  disabled: boolean;
  children: ReactNode;
  isLoading: boolean;
  scrollable: boolean;
  hideSecondary: boolean;
  secondaryLabel: string;
  blurTargetId?: string;
  hidePrimary: boolean;
  primaryLabel: string;
  onClose: (id: string) => void;
  onPrimaryClick?: (e: FormEvent | MouseEvent, id: string) => void;
  onSecondaryClick?: (id: string) => void;
  hideClose: boolean;
  fullscreen: boolean;
  hideHeader: boolean;
  hideFooter: boolean;
  primaryLoading: boolean;
  secondaryLoading: boolean;
  primaryDisabled: boolean;
  secondaryDisabled: boolean;
}

function ModalContent(props: Props) {
  const {
    id,
    title,
    asForm,
    isNaked,
    disabled,
    children,
    isLoading,
    scrollable,
    hideSecondary,
    secondaryLabel,
    blurTargetId,
    hidePrimary,
    primaryLabel,
    hideClose,
    fullscreen,
    hideHeader,
    hideFooter,
    primaryLoading,
    secondaryLoading,
    primaryDisabled,
    secondaryDisabled,
    onClose,
    onPrimaryClick,
    onSecondaryClick,
  } = props;
  /**  */
  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose(id);
    }
  }, [onClose, id]);

  const handleSecondaryClick = useCallback(() => {
    if (typeof onSecondaryClick === 'function') {
      onSecondaryClick(id);
    }
  }, [onSecondaryClick, id]);

  const handlePrimaryClick = useCallback(
    (e) => {
      asForm && e.preventDefault();
      if (typeof onPrimaryClick === 'function') {
        onPrimaryClick(e, id);
      }
    },
    [asForm, onPrimaryClick, id]
  );

  return (
    <Wrapper
      data-cy-id={`modal-${id.replace('/', '-')}`}
      justifyContent="center"
      fullscreen={fullscreen}
      isLoading={isLoading}
      scrollable={isNaked}
      dropOpacity={(blurTargetId && blurTargetId.length > 0) || false}
      width="100%"
    >
      {isLoading ? (
        <Column alignSelf="center">
          <LoadingWarren width={80} />
        </Column>
      ) : isNaked ? (
        children
      ) : (
        <Column
          maxWidth="700px"
          alignSelfLg="center"
          as={asForm ? 'form' : 'section'}
          onSubmit={asForm ? handlePrimaryClick : undefined}
        >
          {hideHeader === false && (
            <ModalHeader
              title={title}
              disabled={disabled}
              hideClose={hideClose}
              onClose={handleClose}
            />
          )}
          <ModalBody scrollable={scrollable} data-cy-id="modal-body">
            {children}
          </ModalBody>
          {hideFooter === false && (
            <ModalFooter
              confirmWithSubmit={asForm}
              disabled={disabled}
              hideSecondary={hideSecondary}
              secondaryLabel={secondaryLabel}
              hidePrimary={hidePrimary}
              primaryLabel={primaryLabel}
              onPrimaryClick={handlePrimaryClick}
              onSecondaryClick={handleSecondaryClick}
              primaryLoading={primaryLoading}
              secondaryLoading={secondaryLoading}
              primaryDisabled={primaryDisabled}
              secondaryDisabled={secondaryDisabled}
            />
          )}
        </Column>
      )}
      {blurTargetId && blurTargetId.length > 0 && (
        <BlurFilter filterId={blurTargetId} />
      )}
    </Wrapper>
  );
}

export default ModalContent;
